import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Palette, AlignLeft, Mail, Loader2, Save, RotateCcw, Eye, ChevronRight, Check, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { DocumentBranding } from "@/components/DocumentBranding";
import { CgvEditor } from "@/components/studio/CgvEditor";
import { studio, apiError, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { PageSkeleton } from "@/components/studio/StudioUI";

const TABS = [
  { id: "apparence", label: "Apparence", icon: Palette },
  { id: "pied", label: "Pied de page", icon: AlignLeft },
  { id: "cgv", label: "CGV", icon: ScrollText },
  { id: "emails", label: "Emails", icon: Mail },
];
const LOGO_SIZES = [["petit", "Petit"], ["moyen", "Moyen"], ["grand", "Grand"]];

const Seg = ({ options, value, onChange, testId }) => (
  <div className="inline-flex rounded-xl border border-[#E2E7F7] bg-white p-1" data-testid={testId}>
    {options.map(([v, l]) => (
      <button
        key={v}
        type="button"
        data-testid={`${testId}-${v}`}
        onClick={() => onChange(v)}
        className={`min-h-[38px] rounded-lg px-4 text-sm font-semibold transition-colors ${value === v ? "bg-[#EEF1FC] text-[#3A5CC8]" : "text-[#5C6A87] hover:text-[#0F2645]"}`}
      >
        {l}
      </button>
    ))}
  </div>
);

export default function StudioDocuments() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("onglet") || "apparence";
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const focusRef = useRef("body");

  useEffect(() => {
    studio.get("/profile").then(({ data }) => setProfile(data)).catch(() => toast.error("Profil introuvable."));
    studio.get("/documents/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const save = async (next = profile, msg = "Réglages enregistrés.") => {
    setBusy("save");
    try {
      const { data } = await studio.put("/profile", { ...next, quote_validity_days: Number(next.quote_validity_days) || 30 });
      setProfile(data);
      toast.success(msg);
      studio.get("/documents/settings").then(({ data: s }) => setSettings(s)).catch(() => {});
      return true;
    } catch (e) { toast.error(apiError(e, "Enregistrement impossible.")); return false; }
    finally { setBusy(""); }
  };

  const insertVar = (k) => {
    const field = focusRef.current;
    const el = document.querySelector(`[data-testid=email-template-${field}]`);
    const text = editing[field];
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    setEditing({ ...editing, [field]: `${text.slice(0, start)}{${k}}${text.slice(end)}` });
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + k.length + 2, start + k.length + 2); });
  };

  const preview = async () => {
    setBusy("preview");
    try {
      await studio.put("/profile", { ...profile, quote_validity_days: Number(profile.quote_validity_days) || 30 });
      const res = await studio.get("/documents/preview", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiError(e, "Aperçu impossible.")); }
    finally { setBusy(""); }
  };

  const toggleHide = (key) => {
    const cur = profile.doc_hide || [];
    setProfile({ ...profile, doc_hide: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] });
  };

  const saveTemplate = async () => {
    const templates = { ...(profile.email_templates || {}) };
    const subject = editing.subject.trim(), body = editing.body.trim();
    const t = settings.templates.find((x) => x.key === editing.key);
    if (subject === t.subject && body === t.body) delete templates[editing.key];
    else templates[editing.key] = { subject, body };
    if (await save({ ...profile, email_templates: templates }, "Modèle d'email enregistré.")) setEditing(null);
  };

  const resetTemplate = async () => {
    const templates = { ...(profile.email_templates || {}) };
    delete templates[editing.key];
    if (await save({ ...profile, email_templates: templates }, "Modèle réinitialisé.")) setEditing(null);
  };

  if (!profile) return <StudioLayout title="Documents" ownerOnly><PageSkeleton /></StudioLayout>;

  return (
    <StudioLayout
      title="Documents"
      subtitle="Apparence de vos devis et factures, pied de page et modèles d'emails envoyés à vos clients."
      ownerOnly
      action={
        <button type="button" data-testid="documents-preview" disabled={busy === "preview"} onClick={preview} className={btnGhost}>
          {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Aperçu PDF
        </button>
      }
    >
      <div className="mt-6 flex gap-1 overflow-x-auto rounded-xl bg-[#E9EDF9] p-1 sm:inline-flex">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`documents-tab-${t.id}`}
            onClick={() => { setParams({ onglet: t.id }); setEditing(null); }}
            className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 text-[13px] font-semibold transition-all sm:px-4 sm:text-sm ${tab === t.id ? "bg-white text-[#3A5CC8] shadow-sm" : "text-[#5C6A87] hover:text-[#0F2645]"}`}
          >
            <t.icon className="hidden h-4 w-4 sm:block" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "cgv" && <CgvEditor profile={profile} setProfile={setProfile} busy={busy === "save"} onSave={() => save(profile, "Conditions générales enregistrées.")} />}

      {tab === "apparence" && (
        <div className="dv-card-in mt-4 space-y-4">
          <DocumentBranding profile={profile} setProfile={setProfile} hideFooter />

          <div data-testid="documents-appearance" className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Taille du logo</p>
                <div className="mt-2"><Seg testId="doc-logo-size" options={LOGO_SIZES} value={profile.doc_logo_size || "moyen"} onChange={(v) => setProfile({ ...profile, doc_logo_size: v })} /></div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Taille du texte</p>
                <div className="mt-2"><Seg testId="doc-text-size" options={[["normale", "Normale"], ["grande", "Grande"]]} value={profile.doc_text_size || "normale"} onChange={(v) => setProfile({ ...profile, doc_text_size: v })} /></div>
              </div>
            </div>

            <p className="mt-6 text-xs font-bold uppercase tracking-wide text-[#5C6A87]">En-tête du document</p>
            <p className="mt-1 text-xs text-[#93A0BC]">Masquez les informations que votre logo affiche déjà.</p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {Object.entries(settings?.hide_fields || {}).map(([key, label]) => {
                const checked = (profile.doc_hide || []).includes(key);
                return (
                  <label key={key} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${checked ? "border-[#3A5CC8]/50 bg-[#EEF1FC]" : "border-[#E2E7F7] bg-white hover:border-[#C9D4F4]"}`}>
                    <input type="checkbox" data-testid={`doc-hide-${key}`} checked={checked} onChange={() => toggleHide(key)} className="h-4 w-4 rounded accent-[#3A5CC8]" />
                    <span className="font-medium text-[#0F2645]">Masquer {label.toLowerCase()}</span>
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end">
              <button type="button" data-testid="documents-save-appearance" disabled={busy === "save"} onClick={() => save()} className={btnPrimary}>
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "pied" && (
        <div data-testid="documents-footer" className="dv-card-in mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <p className="font-heading text-base font-bold text-[#0F2645]">Contenu du pied de page</p>
          <div className="mt-3 space-y-2">
            {[["auto", "Texte généré automatiquement", "Nom, adresse, TVA, IBAN et coordonnées depuis votre profil."], ["libre", "Texte libre", "Vous rédigez le pied de page vous-même."]].map(([v, l, h]) => (
              <label key={v} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-colors ${(profile.doc_footer_mode || "auto") === v ? "border-[#3A5CC8]/50 bg-[#EEF1FC]" : "border-[#E2E7F7] hover:border-[#C9D4F4]"}`}>
                <input type="radio" name="footer-mode" data-testid={`doc-footer-mode-${v}`} checked={(profile.doc_footer_mode || "auto") === v} onChange={() => setProfile({ ...profile, doc_footer_mode: v })} className="mt-1 accent-[#3A5CC8]" />
                <span><span className="block text-sm font-semibold text-[#0F2645]">{l}</span><span className="block text-xs text-[#5C6A87]">{h}</span></span>
              </label>
            ))}
          </div>
          {(profile.doc_footer_mode || "auto") === "auto" ? (
            <p data-testid="doc-footer-auto-preview" className="mt-4 rounded-xl border border-dashed border-[#C9D4F4] bg-[#F6F8FD] px-4 py-3 font-mono text-xs text-[#5C6A87]">
              {settings?.auto_footer || "Complétez votre profil pour générer le pied de page."}
            </p>
          ) : (
            <textarea data-testid="doc-footer-text" rows={3} className={`${inputCls} mt-4`} placeholder="Ex. : Mon Entreprise SRL – Rue Exemple 1, 1000 Bruxelles – TVA BE0123.456.789" value={profile.doc_footer || ""} onChange={(e) => setProfile({ ...profile, doc_footer: e.target.value })} />
          )}
          <div className="mt-5 flex justify-end">
            <button type="button" data-testid="documents-save-footer" disabled={busy === "save"} onClick={() => save()} className={btnPrimary}>
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
            </button>
          </div>
        </div>
      )}

      {tab === "emails" && !editing && (
        <div data-testid="documents-emails" className="dv-card-in mt-4 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <div className="border-b border-[#E2E7F7] px-5 py-4">
            <p className="font-heading text-base font-bold text-[#0F2645]">Modèles d'emails</p>
            <p className="mt-1 text-sm text-[#5C6A87]">Personnalisez simplement les emails envoyés à vos clients. Votre logo est ajouté automatiquement en en-tête.</p>
          </div>
          {(settings?.templates || []).map((t) => {
            const custom = !!(profile.email_templates || {})[t.key];
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`email-template-${t.key}`}
                onClick={() => setEditing({ key: t.key, subject: custom ? profile.email_templates[t.key].subject : t.subject, body: custom ? profile.email_templates[t.key].body : t.body })}
                className="flex w-full items-center gap-3 border-b border-[#E2E7F7] px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-[#F6F8FD]"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-sm font-bold text-[#3A5CC8]">{t.label}{custom && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Personnalisé</span>}</span>
                  <span className="mt-0.5 block text-sm text-[#5C6A87]">{t.description}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </button>
            );
          })}
        </div>
      )}

      {tab === "emails" && editing && (
        <div data-testid="email-template-editor" className="dv-card-in mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <button type="button" data-testid="email-template-back" onClick={() => setEditing(null)} className="text-sm font-semibold text-[#3A5CC8] hover:underline">← Tous les modèles</button>
          <p className="mt-3 font-heading text-base font-bold text-[#0F2645]">{settings.templates.find((t) => t.key === editing.key)?.label}</p>
          <label className="mt-4 block">
            <span className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Objet</span>
            <input data-testid="email-template-subject" className={`${inputCls} mt-1`} onFocus={() => { focusRef.current = "subject"; }} value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
          </label>
          <label className="mt-3 block">
            <span className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Message</span>
            <textarea data-testid="email-template-body" rows={10} className={`${inputCls} mt-1 leading-relaxed`} onFocus={() => { focusRef.current = "body"; }} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
          </label>
          <p className="mt-2 text-[11px] text-[#93A0BC]">Le bouton « {settings.templates.find((t) => t.key === editing.key)?.button} » est ajouté automatiquement sous votre texte.</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(settings.variables).map(([k, l]) => (
              <button key={k} type="button" data-testid={`email-var-${k}`} title={l} onClick={() => insertVar(k)} className="rounded-full border border-[#E2E7F7] bg-[#F6F8FD] px-2.5 py-1 font-mono text-[11px] text-[#2F4CA8] hover:border-[#3A5CC8]">
                {`{${k}}`} <span className="font-sans text-[#93A0BC]">· {l}</span>
              </button>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
            <button type="button" data-testid="email-template-reset" disabled={busy === "save"} onClick={resetTemplate} className={btnGhost}><RotateCcw className="h-3.5 w-3.5" /> Rétablir le modèle par défaut</button>
            <button type="button" data-testid="email-template-save" disabled={busy === "save"} onClick={saveTemplate} className={btnPrimary}>
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
            </button>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
